'use client';

// imports
import { Globe, MapPin, Users, Ribbon } from 'lucide-react';
import CountUp from '@/components/ui/CountUp';
import { useInView } from '@/hooks/useInView';

// constants
const stats = [
  {
    icon: Globe,
    value: 589,
    suffix: 'M+',
    label: 'People with diabetes worldwide',
    sublabel: 'IDF Diabetes Atlas 2025',
    bg: 'bg-blue-50',
    iconColor: 'text-blue-500',
    valueColor: 'text-blue-700',
  },
  {
    icon: MapPin,
    value: 4.7,
    suffix: 'M+',
    label: 'Filipinos affected by diabetes',
    sublabel: 'IDF Diabetes Atlas 2025',
    bg: 'bg-rose-50',
    iconColor: 'text-rose-500',
    valueColor: 'text-rose-700',
  },
  {
    icon: Ribbon,
    value: 252,
    suffix: 'M+',
    label: 'People undiagnosed globally',
    sublabel: 'IDF Diabetes Atlas 2025',
    bg: 'bg-amber-50',
    iconColor: 'text-amber-500',
    valueColor: 'text-amber-700',
  },
  {
    icon: Users,
    value: 43,
    suffix: '%',
    label: 'Adults with undiagnosed diabetes',
    sublabel: 'IDF Diabetes Atlas 2025',
    bg: 'bg-purple-50',
    iconColor: 'text-purple-500',
    valueColor: 'text-purple-700',
  },
];

const base ='transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)]';
const hidden = 'opacity-0 translate-y-8';
const show = 'opacity-100 translate-y-0';

// component
export default function StatsSection() {

  // hooks
  const { ref, isVisible } = useInView<HTMLDivElement>({
    threshold: 0.15,
    once: true,
  });

  return (
    <section ref={ref} className="py-20 lg:py-28 bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 relative overflow-hidden">
      {/* deco */}
      <div className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, #ffffff 1px, transparent 0)`,
          backgroundSize: '32px 32px',
        }}
      />
      
      {/* glow */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl" />

      {/* container */}
      <div className="relative z-10 max-w-screen-xl mx-auto px-6 lg:px-10 xl:px-16">
        
        {/* header */}
        <div className="text-center mb-14">
          <div className={`${base} ${isVisible ? show : hidden} inline-flex items-center gap-2 bg-white/10 border border-white/20 text-blue-300 rounded-full px-4 py-1.5 text-xs font-semibold mb-4`}>
            <Globe size={14} className='text-blue-300' />
            The Diabetes Crisis
          </div>
          <h2 className={`${base} delay-150 ${isVisible ? show : hidden} text-3xl md:text-4xl font-bold text-white mb-4`}>
            The Numbers Demand{' '}
            <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
              Early Action
            </span>
          </h2>
          <p className={`${base} delay-300 ${isVisible ? show : hidden} text-slate-400 text-base max-w-lg mx-auto leading-relaxed`}>
            Diabetes is a global crisis — and millions don't even know they have it. Early detection changes outcomes.
          </p>
        </div>

        {/* stats grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 items-stretch">
          {stats?.map((stat, index) => {
            const Icon = stat?.icon;
            return (
            <div
              key={stat?.label}
              className={`
                h-full
                ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}
                transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)]
              `}
              style={{
                transitionDelay: `${400 + index * 120}ms`,
              }}
            >
              <div className="group bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:bg-white/10 hover:border-white/20 transition-transform transition-colors duration-300 hover:-translate-y-1 text-center h-full">
                
                {/* icon */}
                <div className={`w-12 h-12 ${stat?.bg} rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300`}>
                  <Icon size={20} className={stat?.iconColor} />
                </div>

                {/* value */}
                <p className={`text-4xl md:text-5xl font-bold mb-2 ${stat?.valueColor}`}>
                  {isVisible && (
                    <CountUp to={stat.value} duration={2.5} delay={0.2} />
                  )}
                  {stat.suffix}
                </p>

                {/* label */}
                <p className="text-white font-semibold text-sm mb-1.5 leading-snug">
                  {stat?.label}
                </p>

                {/* sublabel */}
                <p className="text-slate-500 text-xs">
                  {stat?.sublabel}
                </p>
              </div>
            </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}